import { type MouseEvent, memo } from 'react';

import { PlayingCardsFanIcon } from '@air/design-icons';

import { WorkspaceAgentGuidedVariantSizeOptionRow } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/WorkspaceAgentGuidedVariantSizeOptionRow';
import {
  isWorkspaceAgentScaleOrCropSizeSelected,
  WORKSPACE_AGENT_WEB_DOWNSCALE_LABELS,
  type WorkspaceAgentScaleOrCropSearchResults,
  type WorkspaceAgentScaleOrCropSize,
  workspaceAgentScaleOrCropSizeText,
  workspaceAgentScaleOrCropUpscaleChannelId,
  workspaceAgentSmartResizeSizesFromScaleOrCrop,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentScaleOrCrop';
import {
  isWorkspaceAgentSmartResizeSizeSelected,
  sizeSetIsFullySelected,
  workspaceAgentSmartResizeChannelSize,
  workspaceAgentSmartResizeSavedSizeToOptionRowChannel,
  workspaceAgentSmartResizeShapeOptionToOptionRowChannel,
  workspaceAgentSmartResizeSizeSetToOptionRowChannel,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentSmartResize';

export type WorkspaceAgentGuidedVariantScaleOrCropSearchResultsProps = {
  hasSearchResults: boolean;
  onAddTyped: () => void;
  onSelectSet: (event: MouseEvent<HTMLButtonElement>) => void;
  onSelectSize: (event: MouseEvent<HTMLButtonElement>) => void;
  search: WorkspaceAgentScaleOrCropSearchResults;
  sizes: readonly WorkspaceAgentScaleOrCropSize[];
};

export const WorkspaceAgentGuidedVariantScaleOrCropSearchResults = memo(
  ({
    hasSearchResults,
    onAddTyped,
    onSelectSet,
    onSelectSize,
    search,
    sizes,
  }: WorkspaceAgentGuidedVariantScaleOrCropSearchResultsProps) => {
    const smartResizeSizes = workspaceAgentSmartResizeSizesFromScaleOrCrop(sizes);
    const typedText = search.typed ? workspaceAgentScaleOrCropSizeText(search.typed) : '';

    return (
      <fieldset aria-label="Results" className="m-0 flex min-w-0 flex-col border-0 p-0">
        {search.typed ? (
          <WorkspaceAgentGuidedVariantSizeOptionRow
            channel={workspaceAgentSmartResizeChannelSize({
              height: 0,
              id: 'add-typed-size',
              label: typedText,
              ratio: '',
              shortLabel: typedText,
              width: 0,
            })}
            data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_SMART_RESIZE_ADD_TYPED"
            isSelected={false}
            label={
              <>
                <span className="text-gray-9">Add </span>
                {typedText}
              </>
            }
            meta={null}
            onClick={onAddTyped}
          />
        ) : null}
        {search.crop.map((shape) => {
          const { channel, meta } = workspaceAgentSmartResizeShapeOptionToOptionRowChannel(shape);
          const isSelected = isWorkspaceAgentSmartResizeSizeSelected(smartResizeSizes, shape.size);

          return (
            <WorkspaceAgentGuidedVariantSizeOptionRow
              channel={channel}
              isSelected={isSelected}
              key={shape.id}
              meta={meta}
              onClick={onSelectSize}
            />
          );
        })}
        {search.sets.map((sizeSet) => {
          const { channel, meta } = workspaceAgentSmartResizeSizeSetToOptionRowChannel(sizeSet);
          const isSelected = sizeSetIsFullySelected(smartResizeSizes, sizeSet);

          return (
            <WorkspaceAgentGuidedVariantSizeOptionRow
              channel={channel}
              data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_SMART_RESIZE_SET"
              isSelected={isSelected}
              key={sizeSet.id}
              leading={<PlayingCardsFanIcon aria-hidden className="shrink-0 text-gray-9" size="sm" />}
              meta={meta}
              onClick={onSelectSet}
              setId={sizeSet.id}
            />
          );
        })}
        {search.savedSizes.map((saved) => {
          const { channel, meta } = workspaceAgentSmartResizeSavedSizeToOptionRowChannel(saved);
          const isSelected = isWorkspaceAgentSmartResizeSizeSelected(smartResizeSizes, saved.size);

          return (
            <WorkspaceAgentGuidedVariantSizeOptionRow
              channel={channel}
              isSelected={isSelected}
              key={saved.id}
              meta={meta}
              onClick={onSelectSize}
            />
          );
        })}
        {search.upscale.map((size) => (
          <WorkspaceAgentGuidedVariantSizeOptionRow
            channel={workspaceAgentSmartResizeChannelSize({
              height: 0,
              id: workspaceAgentScaleOrCropUpscaleChannelId(size.factor),
              label: `${size.factor}x AI upscale`,
              ratio: '',
              shortLabel: `${size.factor}x AI upscale`,
              width: 0,
            })}
            isSelected={isWorkspaceAgentScaleOrCropSizeSelected(sizes, size)}
            key={size.factor}
            meta={null}
            onClick={onSelectSize}
          />
        ))}
        {search.downscale.map((size) => (
          <WorkspaceAgentGuidedVariantSizeOptionRow
            channel={workspaceAgentSmartResizeChannelSize({
              height: 0,
              id: size.id,
              label: WORKSPACE_AGENT_WEB_DOWNSCALE_LABELS[size.id],
              ratio: '',
              shortLabel: WORKSPACE_AGENT_WEB_DOWNSCALE_LABELS[size.id],
              width: 0,
            })}
            isSelected={isWorkspaceAgentScaleOrCropSizeSelected(sizes, size)}
            key={size.id}
            meta={`${size.px} px`}
            onClick={onSelectSize}
          />
        ))}
        {!hasSearchResults ? (
          <p className="px-2 py-1.5 text-14 text-gray-9">No sizes match. Try Square, 4:5, or 1080x1350.</p>
        ) : null}
      </fieldset>
    );
  },
);

WorkspaceAgentGuidedVariantScaleOrCropSearchResults.displayName = 'WorkspaceAgentGuidedVariantScaleOrCropSearchResults';
